import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faPlus,
  faTrash,
  faEdit,
  faSave,
  faChevronDown,
  faChevronRight,
  faGripVertical,
  faRefresh,
  faCopy,
  faLock,
  faLockOpen,
  faToggleOn,
  faToggleOff,
  faArrowUp,
  faArrowDown
} from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../services/apiService';

// Types
interface AffectedField {
  config: 'rules' | 'scoring';
  fieldKey: string;
  effect: 'enable' | 'constraints' | 'value' | 'unlock';
  constraints?: { min?: number; max?: number };
  value?: any;
}

// UI-specific type for editing that supports both enable and unlock checkboxes
interface UIAffectedField {
  config: 'rules' | 'scoring';
  fieldKey: string;
  enableEffect: boolean;
  unlockEffect: boolean;
  constraintsEffect: boolean;
  valueEffect: boolean;
  constraints?: { min?: number; max?: number };
  value?: any;
}

// Convert AffectedField[] to UIAffectedField[] for editing (group by config+fieldKey)
const toUIAffectedFields = (fields: AffectedField[]): UIAffectedField[] => {
  const map = new Map<string, UIAffectedField>();

  fields.forEach(field => {
    const key = `${field.config}:${field.fieldKey}`;
    if (!map.has(key)) {
      map.set(key, {
        config: field.config,
        fieldKey: field.fieldKey,
        enableEffect: false,
        unlockEffect: false,
        constraintsEffect: false,
        valueEffect: false,
        constraints: undefined,
        value: undefined
      });
    }
    const uiField = map.get(key)!;
    if (field.effect === 'enable') uiField.enableEffect = true;
    if (field.effect === 'unlock') uiField.unlockEffect = true;
    if (field.effect === 'constraints') {
      uiField.constraintsEffect = true;
      uiField.constraints = field.constraints;
    }
    if (field.effect === 'value') {
      uiField.valueEffect = true;
      uiField.value = field.value;
    }
  });

  return Array.from(map.values());
};

// Convert UIAffectedField[] back to AffectedField[] for saving (expand into separate entries)
const fromUIAffectedFields = (uiFields: UIAffectedField[]): AffectedField[] => {
  const result: AffectedField[] = [];

  uiFields.forEach(uiField => {
    if (uiField.enableEffect) {
      result.push({ config: uiField.config, fieldKey: uiField.fieldKey, effect: 'enable' });
    }
    if (uiField.unlockEffect) {
      result.push({ config: uiField.config, fieldKey: uiField.fieldKey, effect: 'unlock' });
    }
    if (uiField.constraintsEffect) {
      result.push({ config: uiField.config, fieldKey: uiField.fieldKey, effect: 'constraints', constraints: uiField.constraints });
    }
    if (uiField.valueEffect) {
      result.push({ config: uiField.config, fieldKey: uiField.fieldKey, effect: 'value', value: uiField.value });
    }
  });

  return result;
};

type UpgradePolicy = 'anytime' | 'before_start' | 'creation_only';

interface Feature {
  key: string;
  name: string;
  description?: string;
  price: number;
  includedIn: string[];
  upgradePolicy: UpgradePolicy;
  affectedFields?: AffectedField[];
  locked: boolean;
  active: boolean;
  isActive?: boolean;
}

interface FeatureCategory {
  key: string;
  name: string;
  description?: string;
  selectionMode: 'single' | 'multi';
  features: Feature[];
  locked: boolean;
  active: boolean;
  isActive?: boolean;
}

interface TierPricing {
  [key: string]: number;
}

interface FeatureRulesData {
  featureCategories: FeatureCategory[];
  tierPricing: TierPricing;
}

// Available plans
const AVAILABLE_PLANS = ['starter', 'pro', 'elite', 'ultimate'];

// Default tier pricing
const DEFAULT_TIER_PRICING: TierPricing = {
  starter: 0,
  pro: 1000,
  elite: 2000,
  ultimate: 3000
};

const PlanRulesConfig: React.FC = () => {
  const [data, setData] = useState<FeatureRulesData>({ featureCategories: [], tierPricing: { ...DEFAULT_TIER_PRICING } });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editingFeature, setEditingFeature] = useState<{ categoryKey: string; featureKey: string } | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategorySelectionMode, setNewCategorySelectionMode] = useState<'single' | 'multi'>('single');
  const [showAddCategory, setShowAddCategory] = useState(false);

  // Add Feature Modal State
  const [showAddFeatureModal, setShowAddFeatureModal] = useState(false);
  const [addFeatureCategoryKey, setAddFeatureCategoryKey] = useState<string | null>(null);
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newFeatureDescription, setNewFeatureDescription] = useState('');
  const [newFeaturePrice, setNewFeaturePrice] = useState('0');
  const [newFeatureIncludedIn, setNewFeatureIncludedIn] = useState<string[]>([]);
  const [newFeatureLocked, setNewFeatureLocked] = useState(false);
  const [newFeatureActive, setNewFeatureActive] = useState(true);
  const [newFeatureUpgradePolicy, setNewFeatureUpgradePolicy] = useState<UpgradePolicy>('anytime');
  const [newFeatureAffectedFields, setNewFeatureAffectedFields] = useState<UIAffectedField[]>([]);

  // Edit Feature Modal State
  const [showEditFeatureModal, setShowEditFeatureModal] = useState(false);
  const [editFeatureCategoryKey, setEditFeatureCategoryKey] = useState<string | null>(null);
  const [editFeatureKey, setEditFeatureKey] = useState<string | null>(null);
  const [editFeatureName, setEditFeatureName] = useState('');
  const [editFeatureDescription, setEditFeatureDescription] = useState('');
  const [editFeaturePrice, setEditFeaturePrice] = useState('0');
  const [editFeatureIncludedIn, setEditFeatureIncludedIn] = useState<string[]>([]);
  const [editFeatureLocked, setEditFeatureLocked] = useState(false);
  const [editFeatureActive, setEditFeatureActive] = useState(true);
  const [editFeatureUpgradePolicy, setEditFeatureUpgradePolicy] = useState<UpgradePolicy>('anytime');
  const [editFeatureAffectedFields, setEditFeatureAffectedFields] = useState<UIAffectedField[]>([]);

  // Available fields from rulesConfig and scoringConfig
  const [availableRulesFields, setAvailableRulesFields] = useState<{ key: string; label: string; section: string }[]>([]);
  const [availableScoringFields, setAvailableScoringFields] = useState<{ key: string; label: string; section: string }[]>([]);
  const [loadingConfigFields, setLoadingConfigFields] = useState(false);
  const [defaultPlanConfig, setDefaultPlanConfig] = useState<FeatureRulesData | null>(null);

  // Template State
  const [templates, setTemplates] = useState<{ _id?: string; id?: string; name: string; createdAt?: string }[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateName, setTemplateName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneTemplateName, setCloneTemplateName] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Reset to empty data
  const resetData = useCallback(() => {
    setData({
      featureCategories: [],
      tierPricing: { ...DEFAULT_TIER_PRICING }
    });
    setExpandedCategories(new Set());
    setSelectedTemplateId('');
  }, []);

  // Load templates list
  const loadTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const response = await apiService.getFeatureRulesTemplates();
      console.log('Templates list:', response);
      setTemplates(response || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // Load available fields from rulesConfig and scoringConfig via /admin/configs
  const loadConfigFields = useCallback(async () => {
    try {
      setLoadingConfigFields(true);

      // Fetch default configs from /admin/configs
      const configs = await apiService.getDefaultConfigs();
      const rulesFields: { key: string; label: string; section: string }[] = [];
      const scoringFields: { key: string; label: string; section: string }[] = [];

      // Parse rulesConfig
      if (configs.rulesConfig) {
        configs.rulesConfig.forEach((section: any) => {
          if (section.fields) {
            section.fields.forEach((field: any) => {
              rulesFields.push({
                key: field.key,
                label: field.label || field.key,
                section: section.section
              });
            });
          }
        });
      }
      setAvailableRulesFields(rulesFields);

      // Parse scoringConfig
      if (configs.scoringConfig) {
        configs.scoringConfig.forEach((section: any) => {
          if (section.fields) {
            section.fields.forEach((field: any) => {
              scoringFields.push({
                key: field.key,
                label: field.label || field.key,
                section: section.section || section.name
              });
            });
          }
        });
      }
      setAvailableScoringFields(scoringFields);

      // Store default planConfig
      if (configs.planConfig) {
        setDefaultPlanConfig(configs.planConfig);
      }

      console.log('Loaded config fields - Rules:', rulesFields.length, 'Scoring:', scoringFields.length);
    } catch (err) {
      console.error('Error loading config fields:', err);
    } finally {
      setLoadingConfigFields(false);
    }
  }, []);

  // Load a specific template
  const loadTemplate = async (templateId: string) => {
    if (!templateId) {
      // Clear selection when "Load Template..." is selected
      setSelectedTemplateId('');
      return;
    }
    try {
      setLoading(true);
      const response = await apiService.getFeatureRulesTemplate(templateId);
      console.log('Template response:', response);
      if (response) {
        // Data is at root level (featureCategories, tierPricing are direct properties)
        const loadedCategories = response.featureCategories || [];
        // Normalize featureCategories: add default selectionMode and filter includedIn to AVAILABLE_PLANS
        const featureCategories = loadedCategories.map((cat: FeatureCategory) => ({
          ...cat,
          selectionMode: cat.selectionMode || 'single',
          features: cat.features.map((feat: Feature) => ({
            ...feat,
            includedIn: (feat.includedIn || []).filter((plan: string) => AVAILABLE_PLANS.includes(plan)),
            upgradePolicy: feat.upgradePolicy || 'anytime',
            affectedFields: feat.affectedFields || [],
            description: feat.description || ''
          }))
        }));
        console.log('Loaded featureCategories with affectedFields:', featureCategories);
        // Normalize tierPricing to only use current AVAILABLE_PLANS
        const loadedTierPricing = response.tierPricing || {};
        const tierPricing: TierPricing = {};
        AVAILABLE_PLANS.forEach(plan => {
          tierPricing[plan] = loadedTierPricing[plan] || 0;
        });

        setData({
          featureCategories,
          tierPricing
        });
        setSelectedTemplateId(templateId);
        // Expand all categories
        const allKeys = new Set<string>(featureCategories.map((c: FeatureCategory) => c.key));
        setExpandedCategories(allKeys);
        toast.success(`Template "${response.name || 'Loaded'}" loaded`);
      }
    } catch (err: any) {
      console.error('Error loading template:', err);
      toast.error(`Failed to load template: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle save - if template loaded, update it; if new, show modal for naming
  const handleSave = () => {
    const hasTierPricing = Object.values(data.tierPricing).some(price => price > 0);
    if (data.featureCategories.length === 0 && !hasTierPricing) {
      toast.error('Nothing to save');
      return;
    }

    if (selectedTemplateId) {
      // Update existing template
      updateExistingTemplate();
    } else {
      // Show modal to name the new template
      setShowSaveModal(true);
    }
  };

  // Update existing template
  const updateExistingTemplate = async () => {
    const template = templates.find(t => (t._id || t.id) === selectedTemplateId);
    if (!template) return;

    try {
      setSaving(true);
      console.log('Updating template with data:', data);
      await apiService.updateFeatureRulesTemplate(selectedTemplateId, template.name, data);
      toast.success(`Template "${template.name}" updated successfully!`);
    } catch (err: any) {
      console.error('Error updating template:', err);
      toast.error(`Failed to update template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Save as new template
  const saveAsNewTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    try {
      setSaving(true);
      console.log('Saving template with data:', data);
      console.log('featureCategories:', data.featureCategories);
      console.log('tierPricing:', data.tierPricing);
      const result = await apiService.saveFeatureRulesTemplate(templateName.trim(), data);
      toast.success(`Template "${templateName}" saved successfully!`);
      setShowSaveModal(false);
      setTemplateName('');
      loadTemplates(); // Refresh templates list
      // Set the newly created template as selected
      if (result?.id) {
        setSelectedTemplateId(result.id);
      }
    } catch (err: any) {
      console.error('Error saving template:', err);
      toast.error(`Failed to save template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const deleteTemplate = async (templateId: string, templateNameToDelete: string) => {
    if (!window.confirm(`Are you sure you want to delete template "${templateNameToDelete}"?`)) {
      return;
    }
    try {
      await apiService.deleteFeatureRulesTemplate(templateId);
      toast.success('Template deleted');
      loadTemplates();
      if (selectedTemplateId === templateId) {
        setSelectedTemplateId('');
      }
    } catch (err: any) {
      console.error('Error deleting template:', err);
      toast.error(`Failed to delete template: ${err.message}`);
    }
  };

  // Clone template - open modal to get new name
  const openCloneModal = () => {
    if (!selectedTemplateId) {
      toast.error('Please select a template to clone');
      return;
    }
    const template = templates.find(t => (t._id || t.id) === selectedTemplateId);
    setCloneTemplateName(template ? `${template.name} (Copy)` : '');
    setShowCloneModal(true);
  };

  // Execute clone
  const cloneTemplate = async () => {
    if (!cloneTemplateName.trim()) {
      toast.error('Please enter a name for the cloned template');
      return;
    }
    try {
      setSaving(true);
      console.log('Cloning template with data:', data);
      const result = await apiService.saveFeatureRulesTemplate(cloneTemplateName.trim(), data);
      toast.success(`Template cloned as "${cloneTemplateName}"`);
      setShowCloneModal(false);
      setCloneTemplateName('');
      loadTemplates();
      // Select the newly cloned template
      if (result?._id || result?.id) {
        setSelectedTemplateId(result._id || result.id);
      }
    } catch (err: any) {
      console.error('Error cloning template:', err);
      toast.error(`Failed to clone template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Load config fields on mount
  useEffect(() => {
    loadConfigFields();
  }, [loadConfigFields]);

  // Generate key from name
  const generateKey = (name: string): string => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  };

  // Toggle category expansion
  const toggleCategory = (key: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Add new category
  const addCategory = () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    const key = generateKey(newCategoryName);
    if (data.featureCategories.some(c => c.key === key)) {
      toast.error('A category with this name already exists');
      return;
    }

    setData(prev => ({
      ...prev,
      featureCategories: [
        ...prev.featureCategories,
        { key, name: newCategoryName.trim(), selectionMode: newCategorySelectionMode, features: [], locked: false, active: true }
      ]
    }));

    setExpandedCategories(prev => new Set<string>([...Array.from(prev), key]));
    setNewCategoryName('');
    setNewCategorySelectionMode('single');
    setShowAddCategory(false);
    toast.success('Category added');
  };

  // Update category name
  const updateCategoryName = (categoryKey: string, newName: string) => {
    setData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map(c =>
        c.key === categoryKey ? { ...c, name: newName } : c
      )
    }));
  };

  // Update category selection mode
  const updateCategorySelectionMode = (categoryKey: string, selectionMode: 'single' | 'multi') => {
    setData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map(c =>
        c.key === categoryKey ? { ...c, selectionMode } : c
      )
    }));
  };

  // Delete category
  const deleteCategory = (categoryKey: string) => {
    if (!window.confirm('Are you sure you want to delete this category and all its features?')) {
      return;
    }

    setData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.filter(c => c.key !== categoryKey)
    }));
    toast.success('Category deleted');
  };

  // Open add feature modal
  const openAddFeatureModal = (categoryKey: string) => {
    setAddFeatureCategoryKey(categoryKey);
    setNewFeatureName('');
    setNewFeatureDescription('');
    setNewFeaturePrice('0');
    setNewFeatureIncludedIn([]);
    setNewFeatureLocked(false);
    setNewFeatureActive(true);
    setNewFeatureUpgradePolicy('anytime');
    setNewFeatureAffectedFields([]);
    setShowAddFeatureModal(true);
  };

  // Close add feature modal
  const closeAddFeatureModal = () => {
    setShowAddFeatureModal(false);
    setAddFeatureCategoryKey(null);
    setNewFeatureName('');
    setNewFeatureDescription('');
    setNewFeaturePrice('0');
    setNewFeatureIncludedIn([]);
    setNewFeatureLocked(false);
    setNewFeatureActive(true);
    setNewFeatureUpgradePolicy('anytime');
    setNewFeatureAffectedFields([]);
  };

  // Open edit feature modal
  const openEditFeatureModal = (categoryKey: string, feature: Feature) => {
    setEditFeatureCategoryKey(categoryKey);
    setEditFeatureKey(feature.key);
    setEditFeatureName(feature.name);
    setEditFeatureDescription(feature.description || '');
    setEditFeaturePrice(String(feature.price || 0));
    setEditFeatureIncludedIn([...feature.includedIn]);
    setEditFeatureLocked(feature.locked);
    setEditFeatureActive(feature.active);
    setEditFeatureUpgradePolicy(feature.upgradePolicy || 'anytime');
    setEditFeatureAffectedFields(feature.affectedFields ? toUIAffectedFields(feature.affectedFields) : []);
    setShowEditFeatureModal(true);
  };

  // Close edit feature modal
  const closeEditFeatureModal = () => {
    setShowEditFeatureModal(false);
    setEditFeatureCategoryKey(null);
    setEditFeatureKey(null);
    setEditFeatureName('');
    setEditFeatureDescription('');
    setEditFeaturePrice('0');
    setEditFeatureIncludedIn([]);
    setEditFeatureLocked(false);
    setEditFeatureActive(true);
    setEditFeatureUpgradePolicy('anytime');
    setEditFeatureAffectedFields([]);
  };

  // Toggle plan for edit feature
  const toggleEditFeaturePlan = (plan: string) => {
    setEditFeatureIncludedIn(prev =>
      prev.includes(plan) ? prev.filter(p => p !== plan) : [...prev, plan]
    );
  };

  // Save edited feature
  const saveEditFeature = () => {
    if (!editFeatureCategoryKey || !editFeatureKey || !editFeatureName.trim()) {
      toast.error('Please enter a feature name');
      return;
    }

    setData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map(c => {
        if (c.key === editFeatureCategoryKey) {
          return {
            ...c,
            features: c.features.map(f => {
              if (f.key === editFeatureKey) {
                return {
                  ...f,
                  name: editFeatureName.trim(),
                  description: editFeatureDescription.trim() || '',
                  price: parseFloat(editFeaturePrice) || 0,
                  includedIn: editFeatureIncludedIn,
                  upgradePolicy: editFeatureUpgradePolicy,
                  affectedFields: editFeatureAffectedFields.length > 0 ? fromUIAffectedFields(editFeatureAffectedFields) : [],
                  locked: editFeatureLocked,
                  active: editFeatureActive
                };
              }
              return f;
            })
          };
        }
        return c;
      })
    }));
    toast.success('Feature updated');
    closeEditFeatureModal();
  };

  // Toggle plan for new feature
  const toggleNewFeaturePlan = (plan: string) => {
    setNewFeatureIncludedIn(prev =>
      prev.includes(plan) ? prev.filter(p => p !== plan) : [...prev, plan]
    );
  };

  // Add feature to category
  const addFeature = () => {
    if (!addFeatureCategoryKey || !newFeatureName.trim()) {
      toast.error('Please enter a feature name');
      return;
    }

    const featureKey = generateKey(newFeatureName);

    setData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map(c => {
        if (c.key === addFeatureCategoryKey) {
          if (c.features.some(f => f.key === featureKey)) {
            toast.error('A feature with this name already exists in this category');
            return c;
          }
          return {
            ...c,
            features: [
              ...c.features,
              {
                key: featureKey,
                name: newFeatureName.trim(),
                description: newFeatureDescription.trim() || '',
                price: parseFloat(newFeaturePrice) || 0,
                includedIn: newFeatureIncludedIn,
                upgradePolicy: newFeatureUpgradePolicy,
                affectedFields: newFeatureAffectedFields.length > 0 ? fromUIAffectedFields(newFeatureAffectedFields) : [],
                locked: newFeatureLocked,
                active: newFeatureActive
              }
            ]
          };
        }
        return c;
      })
    }));
    toast.success('Feature added');
    closeAddFeatureModal();
  };

  // Update feature
  const updateFeature = (categoryKey: string, featureKey: string, updates: Partial<Feature>) => {
    setData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map(c => {
        if (c.key === categoryKey) {
          return {
            ...c,
            features: c.features.map(f =>
              f.key === featureKey ? { ...f, ...updates } : f
            )
          };
        }
        return c;
      })
    }));
  };

  // Delete feature
  const deleteFeature = (categoryKey: string, featureKey: string) => {
    if (!window.confirm('Are you sure you want to delete this feature?')) {
      return;
    }

    setData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map(c => {
        if (c.key === categoryKey) {
          return {
            ...c,
            features: c.features.filter(f => f.key !== featureKey)
          };
        }
        return c;
      })
    }));
    toast.success('Feature deleted');
  };

  // Move feature up or down within a category
  const moveFeature = (categoryKey: string, featureIndex: number, direction: 'up' | 'down') => {
    setData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map(c => {
        if (c.key === categoryKey) {
          const features = [...c.features];
          const newIndex = direction === 'up' ? featureIndex - 1 : featureIndex + 1;

          // Check bounds
          if (newIndex < 0 || newIndex >= features.length) {
            return c;
          }

          // Swap features
          [features[featureIndex], features[newIndex]] = [features[newIndex], features[featureIndex]];

          return {
            ...c,
            features
          };
        }
        return c;
      })
    }));
  };

  // Toggle Category Locked/Active
  const toggleCategoryProperty = (categoryKey: string, property: 'locked' | 'active') => {
    setData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map(c =>
        c.key === categoryKey ? { ...c, [property]: !c[property] } : c
      )
    }));
  };

  // Toggle Feature Locked/Active
  const toggleFeatureProperty = (categoryKey: string, featureKey: string, property: 'locked' | 'active') => {
    setData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map(category => {
        if (category.key !== categoryKey) return category;
        return {
          ...category,
          features: category.features.map(feature =>
            feature.key === featureKey ? { ...feature, [property]: !feature[property] } : feature
          )
        };
      })
    }));
  };

  // Toggle plan inclusion
  const togglePlanInclusion = (categoryKey: string, featureKey: string, plan: string) => {
    setData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map(c => {
        if (c.key === categoryKey) {
          return {
            ...c,
            features: c.features.map(f => {
              if (f.key === featureKey) {
                const includedIn = f.includedIn.includes(plan)
                  ? f.includedIn.filter(p => p !== plan)
                  : [...f.includedIn, plan];
                return { ...f, includedIn };
              }
              return f;
            })
          };
        }
        return c;
      })
    }));
  };

  // Update tier pricing
  const updateTierPrice = (tier: string, price: number) => {
    setData(prev => ({
      ...prev,
      tierPricing: {
        ...prev.tierPricing,
        [tier]: price
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-purple-100 dark:bg-purple-500/20 rounded-lg border border-purple-200 dark:border-purple-500/30">
            <FontAwesomeIcon icon={faCog} className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Plan Rules Configuration</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Manage plan categories, features, pricing, and tier inclusions
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Template Dropdown */}
          <div className="flex items-center gap-2">
            <select
              value={selectedTemplateId}
              onChange={(e) => loadTemplate(e.target.value)}
              disabled={loadingTemplates || loading}
              className="px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
            >
              <option value="">Load Template...</option>
              {templates.map((template) => {
                const templateId = template._id || template.id || '';
                return (
                  <option key={templateId} value={templateId}>
                    {template.name}
                  </option>
                );
              })}
            </select>
            {selectedTemplateId && (
              <>
                <button
                  onClick={openCloneModal}
                  className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                  title="Clone template"
                >
                  <FontAwesomeIcon icon={faCopy} className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    const template = templates.find(t => (t._id || t.id) === selectedTemplateId);
                    if (template) deleteTemplate(selectedTemplateId, template.name);
                  }}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                  title="Delete selected template"
                >
                  <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                </button>
              </>
            )}
          </div>

          <div className="h-8 w-px bg-gray-300 dark:bg-dark-300/40 hidden sm:block" />

          <button
            onClick={() => {
              if (defaultPlanConfig) {
                setData(JSON.parse(JSON.stringify(defaultPlanConfig)));
                setSelectedTemplateId('');
                setExpandedCategories(new Set(defaultPlanConfig.featureCategories.map((c: FeatureCategory) => c.key)));
                toast.success('Default template loaded');
              } else {
                toast.error('Default config not available');
              }
            }}
            disabled={!defaultPlanConfig || loadingConfigFields}
            className="inline-flex items-center px-4 py-2 bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 rounded-lg font-medium hover:bg-purple-200 dark:hover:bg-purple-500/30 transition-colors border border-purple-200 dark:border-purple-500/30 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faCopy} className={`h-4 w-4 mr-2 ${loadingConfigFields ? 'animate-spin' : ''}`} />
            {loadingConfigFields ? 'Loading...' : 'Load Default'}
          </button>
          <button
            onClick={resetData}
            className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-dark-300/40 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-dark-300/60 transition-colors border border-gray-200 dark:border-dark-300/40"
          >
            <FontAwesomeIcon icon={faRefresh} className="h-4 w-4 mr-2" />
            New
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (data.featureCategories.length === 0 && !Object.values(data.tierPricing).some(p => p > 0))}
            className="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-primary-600 text-white rounded-lg font-medium hover:bg-blue-700 dark:hover:bg-primary-700 transition-colors shadow-sm disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faSave} className={`h-4 w-4 mr-2 ${saving ? 'animate-pulse' : ''}`} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Add Category Button */}
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 shadow-sm p-4">
        {showAddCategory ? (
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Category name (e.g., 'No. of Members per league')"
              className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && addCategory()}
              autoFocus
            />
            <select
              value={newCategorySelectionMode}
              onChange={(e) => setNewCategorySelectionMode(e.target.value as 'single' | 'multi')}
              className="px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="single">Single Selection</option>
              <option value="multi">Multi Selection</option>
            </select>
            <button
              onClick={addCategory}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Add
            </button>
            <button
              onClick={() => { setShowAddCategory(false); setNewCategoryName(''); setNewCategorySelectionMode('single'); }}
              className="px-4 py-2 bg-gray-300 dark:bg-dark-300/40 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-dark-300/60 transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddCategory(true)}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} className="h-4 w-4 mr-2" />
            Add New Category
          </button>
        )}
      </div>

      {/* Tier Pricing Section */}
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 shadow-sm overflow-hidden">
        <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-dark-300/40">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tier Pricing</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Set the credits required for each subscription tier</p>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {AVAILABLE_PLANS.map((plan) => (
              <div
                key={plan}
                className="p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg border border-gray-200 dark:border-dark-300/40"
              >
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 capitalize">
                  {plan} Plan
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={data.tierPricing[plan] || 0}
                    onChange={(e) => updateTierPrice(plan, parseFloat(e.target.value) || 0)}
                    min="0"
                    step="1"
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-center"
                    placeholder="0"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">credits</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Categories List */}
      <div className="space-y-4">
        {data.featureCategories.length === 0 ? (
          <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 shadow-sm p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">No feature categories defined yet. Add your first category above.</p>
          </div>
        ) : (
          data.featureCategories.map((category) => (
            <div
              key={category.key}
              className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 shadow-sm overflow-hidden"
            >
              {/* Category Header */}
              <div className={`flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-400/30 border-b border-gray-200 dark:border-dark-300/40 ${!category.active ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon
                    icon={faGripVertical}
                    className="h-4 w-4 text-gray-400 cursor-grab"
                  />
                  <button
                    onClick={() => toggleCategory(category.key)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-dark-300/40 rounded transition-colors"
                  >
                    <FontAwesomeIcon
                      icon={expandedCategories.has(category.key) ? faChevronDown : faChevronRight}
                      className="h-4 w-4 text-gray-600 dark:text-gray-400"
                    />
                  </button>
                  {editingCategory === category.key ? (
                    <input
                      type="text"
                      value={category.name}
                      onChange={(e) => updateCategoryName(category.key, e.target.value)}
                      onBlur={() => setEditingCategory(null)}
                      onKeyDown={(e) => e.key === 'Enter' && setEditingCategory(null)}
                      className="px-2 py-1 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  ) : (
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{category.name}</h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Key: {category.key}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Active Toggle */}
                  <button
                    onClick={() => toggleCategoryProperty(category.key, 'active')}
                    className={`p-1.5 rounded transition-colors ${category.active ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    title={category.active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                  >
                    <FontAwesomeIcon icon={category.active ? faToggleOn : faToggleOff} className="h-5 w-5" />
                  </button>
                  {/* Locked Toggle */}
                  <button
                    onClick={() => toggleCategoryProperty(category.key, 'locked')}
                    className={`p-1.5 rounded transition-colors ${category.locked ? 'text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    title={category.locked ? 'Locked - Click to unlock' : 'Unlocked - Click to lock'}
                  >
                    <FontAwesomeIcon icon={category.locked ? faLock : faLockOpen} className="h-4 w-4" />
                  </button>
                  <div className="w-px h-5 bg-gray-300 dark:bg-dark-300 mx-1" />
                  <select
                    value={category.selectionMode || 'single'}
                    onChange={(e) => updateCategorySelectionMode(category.key, e.target.value as 'single' | 'multi')}
                    onClick={(e) => e.stopPropagation()}
                    className={`px-2 py-1 text-xs font-medium rounded border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 ${
                      category.selectionMode === 'multi'
                        ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-400'
                        : 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-400'
                    }`}
                  >
                    <option value="single">Single Select</option>
                    <option value="multi">Multi Select</option>
                  </select>
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-400 text-xs font-medium rounded">
                    {category.features.length} features
                  </span>
                  <button
                    onClick={() => setEditingCategory(category.key)}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded transition-colors"
                    title="Edit category name"
                  >
                    <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteCategory(category.key)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
                    title="Delete category"
                  >
                    <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Category Features */}
              {expandedCategories.has(category.key) && (
                <div className="p-4">
                  {/* Features Table Header */}
                  <div className="grid grid-cols-12 gap-4 mb-3 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <div className="col-span-1 text-center">Order</div>
                    <div className="col-span-3">Feature Name</div>
                    <div className="col-span-1 text-center">Credits</div>
                    <div className="col-span-2 text-center">Upgrade Policy</div>
                    <div className="col-span-4">Included In Plans</div>
                    <div className="col-span-1 text-center">Actions</div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-2">
                    {category.features.length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                        No features in this category. Add your first feature below.
                      </p>
                    ) : (
                      category.features.map((feature, featureIndex) => (
                        <div
                          key={feature.key}
                          className={`p-3 bg-gray-50 dark:bg-dark-400/20 rounded-lg border border-gray-100 dark:border-dark-300/20 ${!feature.active ? 'opacity-50' : ''} ${feature.locked ? 'border-l-4 border-l-red-500' : ''}`}
                        >
                          <div className="grid grid-cols-12 gap-4 items-center">
                            {/* Reorder Buttons */}
                            <div className="col-span-1 flex flex-col items-center gap-0.5">
                              <button
                                onClick={() => moveFeature(category.key, featureIndex, 'up')}
                                disabled={featureIndex === 0}
                                className={`p-1 rounded transition-colors ${featureIndex === 0 ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10'}`}
                                title="Move up"
                              >
                                <FontAwesomeIcon icon={faArrowUp} className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => moveFeature(category.key, featureIndex, 'down')}
                                disabled={featureIndex === category.features.length - 1}
                                className={`p-1 rounded transition-colors ${featureIndex === category.features.length - 1 ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10'}`}
                                title="Move down"
                              >
                                <FontAwesomeIcon icon={faArrowDown} className="h-3 w-3" />
                              </button>
                            </div>

                            {/* Feature Name */}
                            <div className="col-span-3">
                              {editingFeature?.categoryKey === category.key && editingFeature?.featureKey === feature.key ? (
                                <input
                                  type="text"
                                  value={feature.name}
                                  onChange={(e) => updateFeature(category.key, feature.key, { name: e.target.value })}
                                  onBlur={() => setEditingFeature(null)}
                                  onKeyDown={(e) => e.key === 'Enter' && setEditingFeature(null)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  autoFocus
                                />
                              ) : (
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-white text-sm">{feature.name}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{feature.key}</p>
                                </div>
                              )}
                            </div>

                            {/* Credits */}
                            <div className="col-span-1">
                              <input
                                type="number"
                                value={feature.price}
                                onChange={(e) => updateFeature(category.key, feature.key, {
                                  price: parseFloat(e.target.value) || 0
                                })}
                                min="0"
                                step="1"
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                              />
                            </div>

                            {/* Upgrade Policy */}
                            <div className="col-span-2 flex justify-center">
                              <select
                                value={feature.upgradePolicy || 'anytime'}
                                onChange={(e) => updateFeature(category.key, feature.key, {
                                  upgradePolicy: e.target.value as UpgradePolicy
                                })}
                                className={`px-2 py-1.5 text-xs font-medium rounded border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 ${
                                  feature.upgradePolicy === 'anytime'
                                    ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-400'
                                    : feature.upgradePolicy === 'before_start'
                                    ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-400'
                                    : 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-400'
                                }`}
                              >
                                <option value="anytime">Anytime</option>
                                <option value="before_start">Before Start</option>
                                <option value="creation_only">Creation Only</option>
                              </select>
                            </div>

                            {/* Plan Checkboxes */}
                            <div className="col-span-4 flex flex-wrap gap-2">
                              {AVAILABLE_PLANS.map((plan) => (
                                <label
                                  key={plan}
                                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                                    feature.includedIn.includes(plan)
                                      ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-400 border border-green-300 dark:border-green-500/30'
                                      : 'bg-gray-100 dark:bg-dark-300/40 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-300/40 hover:bg-gray-200 dark:hover:bg-dark-300/60'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={feature.includedIn.includes(plan)}
                                    onChange={() => togglePlanInclusion(category.key, feature.key, plan)}
                                    className="sr-only"
                                  />
                                  <span className="capitalize">{plan}</span>
                                </label>
                              ))}
                            </div>

                            {/* Actions */}
                            <div className="col-span-1 flex justify-center gap-1">
                              {/* Active Toggle */}
                              <button
                                onClick={() => toggleFeatureProperty(category.key, feature.key, 'active')}
                                className={`p-1.5 rounded transition-colors ${feature.active ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                title={feature.active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                              >
                                <FontAwesomeIcon icon={feature.active ? faToggleOn : faToggleOff} className="h-4 w-4" />
                              </button>
                              {/* Locked Toggle */}
                              <button
                                onClick={() => toggleFeatureProperty(category.key, feature.key, 'locked')}
                                className={`p-1.5 rounded transition-colors ${feature.locked ? 'text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                title={feature.locked ? 'Locked - Click to unlock' : 'Unlocked - Click to lock'}
                              >
                                <FontAwesomeIcon icon={feature.locked ? faLock : faLockOpen} className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => openEditFeatureModal(category.key, feature)}
                                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded transition-colors"
                                title="Edit feature"
                              >
                                <FontAwesomeIcon icon={faEdit} className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => deleteFeature(category.key, feature.key)}
                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
                                title="Delete feature"
                              >
                                <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Affected Fields Display */}
                          {feature.affectedFields && feature.affectedFields.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-dark-300/30">
                              <div className="flex flex-wrap gap-2">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Affects:</span>
                                {feature.affectedFields.map((af, idx) => (
                                  <span
                                    key={idx}
                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                      af.config === 'rules'
                                        ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400'
                                        : 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400'
                                    }`}
                                    title={`Config: ${af.config}, Effect: ${af.effect}${af.effect === 'constraints' ? `, Min: ${af.constraints?.min ?? '-'}, Max: ${af.constraints?.max ?? '-'}` : ''}${af.effect === 'value' ? `, Value: ${af.value}` : ''}`}
                                  >
                                    <span className="font-semibold mr-1">{af.config === 'rules' ? 'R' : 'S'}:</span>
                                    {af.fieldKey}
                                    {af.effect === 'constraints' && (
                                      <span className="ml-1 text-[10px] opacity-75">
                                        ({af.constraints?.min ?? '?'}-{af.constraints?.max ?? '?'})
                                      </span>
                                    )}
                                    {af.effect === 'enable' && (
                                      <span className="ml-1 text-[10px] opacity-75">(enable)</span>
                                    )}
                                    {af.effect === 'value' && (
                                      <span className="ml-1 text-[10px] opacity-75">(={af.value})</span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Feature Button */}
                  <button
                    onClick={() => openAddFeatureModal(category.key)}
                    className="mt-4 inline-flex items-center px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors border border-blue-200 dark:border-blue-500/30"
                  >
                    <FontAwesomeIcon icon={faPlus} className="h-3 w-3 mr-1.5" />
                    Add Feature
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* JSON Preview (collapsible) */}
      <details className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 shadow-sm">
        <summary className="p-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-dark-400/30 transition-colors">
          JSON Preview
        </summary>
        <pre className="p-4 bg-gray-900 text-green-400 text-xs overflow-auto max-h-96 rounded-b-lg">
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>

      {/* Add Feature Modal */}
      {showAddFeatureModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-900/50 dark:bg-black/70 backdrop-blur-sm transition-opacity"
              onClick={closeAddFeatureModal}
            />

            {/* Modal */}
            <div className="relative inline-block w-full max-w-3xl p-6 my-8 text-left align-middle bg-white dark:bg-dark-500 rounded-xl shadow-xl transform transition-all">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Add New Feature
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Category: {data.featureCategories.find(c => c.key === addFeatureCategoryKey)?.name}
                  </p>
                </div>
                <button
                  onClick={closeAddFeatureModal}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400/30 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Form */}
              <div className="space-y-5">
                {/* Feature Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Feature Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newFeatureName}
                    onChange={(e) => setNewFeatureName(e.target.value)}
                    placeholder="e.g., Upto 15 Members"
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                  {newFeatureName && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Key: {generateKey(newFeatureName)}
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newFeatureDescription}
                    onChange={(e) => setNewFeatureDescription(e.target.value)}
                    placeholder="Optional description for this feature"
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Credits */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Credits
                  </label>
                  <div className="relative max-w-xs">
                    <input
                      type="number"
                      value={newFeaturePrice}
                      onChange={(e) => setNewFeaturePrice(e.target.value)}
                      min="0"
                      step="1"
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">credits</span>
                  </div>
                </div>

                {/* Included In Plans */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Included In Plans
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_PLANS.map((plan) => (
                      <button
                        key={plan}
                        type="button"
                        onClick={() => toggleNewFeaturePlan(plan)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          newFeatureIncludedIn.includes(plan)
                            ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-400 border-2 border-green-500'
                            : 'bg-gray-100 dark:bg-dark-300/40 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-300/40 hover:bg-gray-200 dark:hover:bg-dark-300/60'
                        }`}
                      >
                        <span className="capitalize">{plan}</span>
                        {newFeatureIncludedIn.includes(plan) && (
                          <svg className="inline-block w-4 h-4 ml-1.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Upgrade Policy */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Upgrade Policy
                  </label>
                  <select
                    value={newFeatureUpgradePolicy}
                    onChange={(e) => setNewFeatureUpgradePolicy(e.target.value as UpgradePolicy)}
                    className="w-full max-w-xs px-4 py-2.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="anytime">Anytime — can upgrade at any point</option>
                    <option value="before_start">Before Start — only before first match</option>
                    <option value="creation_only">Creation Only — locked at creation</option>
                  </select>
                </div>

                {/* Flags */}
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newFeatureActive}
                      onChange={(e) => setNewFeatureActive(e.target.checked)}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newFeatureLocked}
                      onChange={(e) => setNewFeatureLocked(e.target.checked)}
                      className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Locked</span>
                  </label>
                </div>

                {/* Affected Fields */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Affected Fields
                    </label>
                    <button
                      type="button"
                      onClick={() => setNewFeatureAffectedFields([...newFeatureAffectedFields, { config: 'rules', fieldKey: '', enableEffect: true, unlockEffect: false, constraintsEffect: false, valueEffect: false }])}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                    >
                      <FontAwesomeIcon icon={faPlus} className="h-3 w-3 mr-1" />
                      Add Field
                    </button>
                  </div>
                  {loadingConfigFields && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Loading available fields...</p>
                  )}
                  {newFeatureAffectedFields.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic py-2">
                      No affected fields. Click "Add Field" to link this feature to config fields.
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-48 overflow-y-auto">
                      {newFeatureAffectedFields.map((field, index) => (
                        <div key={index} className="p-3 bg-gray-50 dark:bg-dark-400/30 rounded-lg border border-gray-200 dark:border-dark-300/40">
                          <div className="flex items-start gap-2 flex-wrap">
                            {/* Config Type */}
                            <select
                              value={field.config}
                              onChange={(e) => {
                                const updated = [...newFeatureAffectedFields];
                                updated[index] = { ...updated[index], config: e.target.value as 'rules' | 'scoring', fieldKey: '' };
                                setNewFeatureAffectedFields(updated);
                              }}
                              className="px-2 py-1.5 text-xs border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="rules">Rules</option>
                              <option value="scoring">Scoring</option>
                            </select>

                            {/* Field Key */}
                            <select
                              value={field.fieldKey}
                              onChange={(e) => {
                                const updated = [...newFeatureAffectedFields];
                                updated[index] = { ...updated[index], fieldKey: e.target.value };
                                setNewFeatureAffectedFields(updated);
                              }}
                              className="flex-1 min-w-[200px] px-2 py-1.5 text-xs border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select field...</option>
                              {(field.config === 'rules' ? availableRulesFields : availableScoringFields).map((f) => (
                                <option key={f.key} value={f.key}>
                                  {f.label} ({f.section})
                                </option>
                              ))}
                            </select>

                            {/* Effects - Enable & Unlock checkboxes */}
                            <div className="flex items-center gap-3 px-2 py-1 bg-gray-100 dark:bg-dark-400/40 rounded border border-gray-300 dark:border-dark-300/40">
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={field.enableEffect}
                                  onChange={(e) => {
                                    const updated = [...newFeatureAffectedFields];
                                    updated[index] = { ...updated[index], enableEffect: e.target.checked };
                                    setNewFeatureAffectedFields(updated);
                                  }}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Enable</span>
                              </label>
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={field.unlockEffect}
                                  onChange={(e) => {
                                    const updated = [...newFeatureAffectedFields];
                                    updated[index] = { ...updated[index], unlockEffect: e.target.checked };
                                    setNewFeatureAffectedFields(updated);
                                  }}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Unlock</span>
                              </label>
                            </div>

                            {/* Constraints checkbox */}
                            <label className="flex items-center gap-1.5 cursor-pointer px-2 py-1 bg-gray-100 dark:bg-dark-400/40 rounded border border-gray-300 dark:border-dark-300/40">
                              <input
                                type="checkbox"
                                checked={field.constraintsEffect}
                                onChange={(e) => {
                                  const updated = [...newFeatureAffectedFields];
                                  updated[index] = { ...updated[index], constraintsEffect: e.target.checked };
                                  setNewFeatureAffectedFields(updated);
                                }}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              />
                              <span className="text-xs text-gray-700 dark:text-gray-300">Constraints</span>
                            </label>

                            {/* Value checkbox */}
                            <label className="flex items-center gap-1.5 cursor-pointer px-2 py-1 bg-gray-100 dark:bg-dark-400/40 rounded border border-gray-300 dark:border-dark-300/40">
                              <input
                                type="checkbox"
                                checked={field.valueEffect}
                                onChange={(e) => {
                                  const updated = [...newFeatureAffectedFields];
                                  updated[index] = { ...updated[index], valueEffect: e.target.checked };
                                  setNewFeatureAffectedFields(updated);
                                }}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <span className="text-xs text-gray-700 dark:text-gray-300">Value</span>
                            </label>

                            {/* Remove Button */}
                            <button
                              type="button"
                              onClick={() => {
                                setNewFeatureAffectedFields(newFeatureAffectedFields.filter((_, i) => i !== index));
                              }}
                              className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
                            >
                              <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                            </button>
                          </div>

                          {/* Constraints fields */}
                          {field.constraintsEffect && (
                            <div className="flex gap-3 mt-2">
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-gray-500 dark:text-gray-400">Min:</label>
                                <input
                                  type="number"
                                  value={field.constraints?.min ?? ''}
                                  onChange={(e) => {
                                    const updated = [...newFeatureAffectedFields];
                                    updated[index] = {
                                      ...updated[index],
                                      constraints: {
                                        ...updated[index].constraints,
                                        min: e.target.value ? parseInt(e.target.value) : undefined
                                      }
                                    };
                                    setNewFeatureAffectedFields(updated);
                                  }}
                                  className="w-20 px-2 py-1 text-xs border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="0"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-gray-500 dark:text-gray-400">Max:</label>
                                <input
                                  type="number"
                                  value={field.constraints?.max ?? ''}
                                  onChange={(e) => {
                                    const updated = [...newFeatureAffectedFields];
                                    updated[index] = {
                                      ...updated[index],
                                      constraints: {
                                        ...updated[index].constraints,
                                        max: e.target.value ? parseInt(e.target.value) : undefined
                                      }
                                    };
                                    setNewFeatureAffectedFields(updated);
                                  }}
                                  className="w-20 px-2 py-1 text-xs border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="100"
                                />
                              </div>
                            </div>
                          )}

                          {/* Value field */}
                          {field.valueEffect && (
                            <div className="flex items-center gap-2 mt-2">
                              <label className="text-xs text-gray-500 dark:text-gray-400">Value:</label>
                              <input
                                type="text"
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const updated = [...newFeatureAffectedFields];
                                  updated[index] = { ...updated[index], value: e.target.value };
                                  setNewFeatureAffectedFields(updated);
                                }}
                                className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter value"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200 dark:border-dark-300/40">
                <button
                  onClick={closeAddFeatureModal}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-300/40 hover:bg-gray-200 dark:hover:bg-dark-300/60 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={addFeature}
                  disabled={!newFeatureName.trim()}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5 mr-2" />
                  Add Feature
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-900/50 dark:bg-black/70 backdrop-blur-sm transition-opacity"
              onClick={() => setShowSaveModal(false)}
            />

            {/* Modal */}
            <div className="relative inline-block w-full max-w-md p-6 my-8 text-left align-middle bg-white dark:bg-dark-500 rounded-xl shadow-xl transform transition-all">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Save as Template
                </h3>
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400/30 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Template Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="e.g., Basic League Features"
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && templateName.trim() && saveAsNewTemplate()}
                  />
                </div>

                <div className="bg-gray-50 dark:bg-dark-400/30 rounded-lg p-3 space-y-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    This will save the current configuration with <strong>{data.featureCategories.length}</strong> categories
                    and <strong>{data.featureCategories.reduce((sum, c) => sum + c.features.length, 0)}</strong> features.
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Tier pricing: {AVAILABLE_PLANS.map(p => `${p}: ${data.tierPricing[p] || 0}`).join(', ')} credits
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-dark-300/40">
                <button
                  onClick={() => { setShowSaveModal(false); setTemplateName(''); }}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-300/40 hover:bg-gray-200 dark:hover:bg-dark-300/60 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAsNewTemplate}
                  disabled={!templateName.trim() || saving}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faSave} className={`h-3.5 w-3.5 mr-2 ${saving ? 'animate-pulse' : ''}`} />
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clone Template Modal */}
      {showCloneModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-900/50 dark:bg-black/70 backdrop-blur-sm transition-opacity"
              onClick={() => setShowCloneModal(false)}
            />

            {/* Modal */}
            <div className="relative inline-block w-full max-w-md p-6 my-8 text-left align-middle bg-white dark:bg-dark-500 rounded-xl shadow-xl transform transition-all">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Clone Template
                </h3>
                <button
                  onClick={() => setShowCloneModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400/30 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    New Template Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={cloneTemplateName}
                    onChange={(e) => setCloneTemplateName(e.target.value)}
                    placeholder="Enter name for cloned template"
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && cloneTemplateName.trim() && cloneTemplate()}
                  />
                </div>

                <div className="bg-blue-50 dark:bg-blue-500/10 rounded-lg p-3">
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    This will create a copy of the current template with the new name.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-dark-300/40">
                <button
                  onClick={() => { setShowCloneModal(false); setCloneTemplateName(''); }}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-300/40 hover:bg-gray-200 dark:hover:bg-dark-300/60 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={cloneTemplate}
                  disabled={!cloneTemplateName.trim() || saving}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faCopy} className={`h-3.5 w-3.5 mr-2 ${saving ? 'animate-pulse' : ''}`} />
                  {saving ? 'Cloning...' : 'Clone Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Feature Modal */}
      {showEditFeatureModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-900/50 dark:bg-black/70 backdrop-blur-sm transition-opacity"
              onClick={closeEditFeatureModal}
            />

            {/* Modal */}
            <div className="relative inline-block w-full max-w-3xl p-6 my-8 text-left align-middle bg-white dark:bg-dark-500 rounded-xl shadow-xl transform transition-all max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Edit Feature
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Key: {editFeatureKey}
                  </p>
                </div>
                <button
                  onClick={closeEditFeatureModal}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400/30 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Form */}
              <div className="space-y-5">
                {/* Feature Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Feature Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFeatureName}
                    onChange={(e) => setEditFeatureName(e.target.value)}
                    placeholder="e.g., Upto 15 Members"
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={editFeatureDescription}
                    onChange={(e) => setEditFeatureDescription(e.target.value)}
                    placeholder="Optional description for this feature"
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Credits */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Credits
                  </label>
                  <div className="relative max-w-xs">
                    <input
                      type="number"
                      value={editFeaturePrice}
                      onChange={(e) => setEditFeaturePrice(e.target.value)}
                      min="0"
                      step="1"
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">credits</span>
                  </div>
                </div>

                {/* Included In Plans */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Included In Plans
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_PLANS.map((plan) => (
                      <button
                        key={plan}
                        type="button"
                        onClick={() => toggleEditFeaturePlan(plan)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          editFeatureIncludedIn.includes(plan)
                            ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-400 border-2 border-green-500'
                            : 'bg-gray-100 dark:bg-dark-300/40 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-300/40 hover:bg-gray-200 dark:hover:bg-dark-300/60'
                        }`}
                      >
                        <span className="capitalize">{plan}</span>
                        {editFeatureIncludedIn.includes(plan) && (
                          <svg className="inline-block w-4 h-4 ml-1.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Upgrade Policy */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Upgrade Policy
                  </label>
                  <select
                    value={editFeatureUpgradePolicy}
                    onChange={(e) => setEditFeatureUpgradePolicy(e.target.value as UpgradePolicy)}
                    className="w-full max-w-xs px-4 py-2.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="anytime">Anytime — can upgrade at any point</option>
                    <option value="before_start">Before Start — only before first match</option>
                    <option value="creation_only">Creation Only — locked at creation</option>
                  </select>
                </div>

                {/* Flags */}
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFeatureActive}
                      onChange={(e) => setEditFeatureActive(e.target.checked)}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFeatureLocked}
                      onChange={(e) => setEditFeatureLocked(e.target.checked)}
                      className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Locked</span>
                  </label>
                </div>

                {/* Affected Fields */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Affected Fields
                    </label>
                    <button
                      type="button"
                      onClick={() => setEditFeatureAffectedFields([...editFeatureAffectedFields, { config: 'rules', fieldKey: '', enableEffect: true, unlockEffect: false, constraintsEffect: false, valueEffect: false }])}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                    >
                      <FontAwesomeIcon icon={faPlus} className="h-3 w-3 mr-1" />
                      Add Field
                    </button>
                  </div>
                  {editFeatureAffectedFields.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic py-2">
                      No affected fields. Click "Add Field" to link this feature to config fields.
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-48 overflow-y-auto">
                      {editFeatureAffectedFields.map((field, index) => (
                        <div key={index} className="p-3 bg-gray-50 dark:bg-dark-400/30 rounded-lg border border-gray-200 dark:border-dark-300/40">
                          <div className="flex items-start gap-2">
                            {/* Config Type */}
                            <select
                              value={field.config}
                              onChange={(e) => {
                                const updated = [...editFeatureAffectedFields];
                                updated[index] = { ...updated[index], config: e.target.value as 'rules' | 'scoring', fieldKey: '' };
                                setEditFeatureAffectedFields(updated);
                              }}
                              className="px-2 py-1.5 text-xs border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="rules">Rules</option>
                              <option value="scoring">Scoring</option>
                            </select>

                            {/* Field Key */}
                            <select
                              value={field.fieldKey}
                              onChange={(e) => {
                                const updated = [...editFeatureAffectedFields];
                                updated[index] = { ...updated[index], fieldKey: e.target.value };
                                setEditFeatureAffectedFields(updated);
                              }}
                              className="flex-1 min-w-[200px] px-2 py-1.5 text-xs border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select field...</option>
                              {(field.config === 'rules' ? availableRulesFields : availableScoringFields).map((f) => (
                                <option key={f.key} value={f.key}>
                                  {f.label} ({f.section})
                                </option>
                              ))}
                            </select>

                            {/* Effects - Enable & Unlock checkboxes */}
                            <div className="flex items-center gap-3 px-2 py-1 bg-gray-100 dark:bg-dark-400/40 rounded border border-gray-300 dark:border-dark-300/40">
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={field.enableEffect}
                                  onChange={(e) => {
                                    const updated = [...editFeatureAffectedFields];
                                    updated[index] = { ...updated[index], enableEffect: e.target.checked };
                                    setEditFeatureAffectedFields(updated);
                                  }}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Enable</span>
                              </label>
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={field.unlockEffect}
                                  onChange={(e) => {
                                    const updated = [...editFeatureAffectedFields];
                                    updated[index] = { ...updated[index], unlockEffect: e.target.checked };
                                    setEditFeatureAffectedFields(updated);
                                  }}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Unlock</span>
                              </label>
                            </div>

                            {/* Constraints checkbox */}
                            <label className="flex items-center gap-1.5 cursor-pointer px-2 py-1 bg-gray-100 dark:bg-dark-400/40 rounded border border-gray-300 dark:border-dark-300/40">
                              <input
                                type="checkbox"
                                checked={field.constraintsEffect}
                                onChange={(e) => {
                                  const updated = [...editFeatureAffectedFields];
                                  updated[index] = { ...updated[index], constraintsEffect: e.target.checked };
                                  setEditFeatureAffectedFields(updated);
                                }}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              />
                              <span className="text-xs text-gray-700 dark:text-gray-300">Constraints</span>
                            </label>

                            {/* Value checkbox */}
                            <label className="flex items-center gap-1.5 cursor-pointer px-2 py-1 bg-gray-100 dark:bg-dark-400/40 rounded border border-gray-300 dark:border-dark-300/40">
                              <input
                                type="checkbox"
                                checked={field.valueEffect}
                                onChange={(e) => {
                                  const updated = [...editFeatureAffectedFields];
                                  updated[index] = { ...updated[index], valueEffect: e.target.checked };
                                  setEditFeatureAffectedFields(updated);
                                }}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <span className="text-xs text-gray-700 dark:text-gray-300">Value</span>
                            </label>

                            {/* Remove Button */}
                            <button
                              type="button"
                              onClick={() => {
                                setEditFeatureAffectedFields(editFeatureAffectedFields.filter((_, i) => i !== index));
                              }}
                              className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
                            >
                              <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                            </button>
                          </div>

                          {/* Constraints fields */}
                          {field.constraintsEffect && (
                            <div className="flex gap-3 mt-2">
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-gray-500 dark:text-gray-400">Min:</label>
                                <input
                                  type="number"
                                  value={field.constraints?.min ?? ''}
                                  onChange={(e) => {
                                    const updated = [...editFeatureAffectedFields];
                                    updated[index] = {
                                      ...updated[index],
                                      constraints: {
                                        ...updated[index].constraints,
                                        min: e.target.value ? parseInt(e.target.value) : undefined
                                      }
                                    };
                                    setEditFeatureAffectedFields(updated);
                                  }}
                                  className="w-20 px-2 py-1 text-xs border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="0"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-gray-500 dark:text-gray-400">Max:</label>
                                <input
                                  type="number"
                                  value={field.constraints?.max ?? ''}
                                  onChange={(e) => {
                                    const updated = [...editFeatureAffectedFields];
                                    updated[index] = {
                                      ...updated[index],
                                      constraints: {
                                        ...updated[index].constraints,
                                        max: e.target.value ? parseInt(e.target.value) : undefined
                                      }
                                    };
                                    setEditFeatureAffectedFields(updated);
                                  }}
                                  className="w-20 px-2 py-1 text-xs border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="100"
                                />
                              </div>
                            </div>
                          )}

                          {/* Value field */}
                          {field.valueEffect && (
                            <div className="flex items-center gap-2 mt-2">
                              <label className="text-xs text-gray-500 dark:text-gray-400">Value:</label>
                              <input
                                type="text"
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const updated = [...editFeatureAffectedFields];
                                  updated[index] = { ...updated[index], value: e.target.value };
                                  setEditFeatureAffectedFields(updated);
                                }}
                                className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter value"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200 dark:border-dark-300/40">
                <button
                  onClick={closeEditFeatureModal}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-300/40 hover:bg-gray-200 dark:hover:bg-dark-300/60 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEditFeature}
                  disabled={!editFeatureName.trim()}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faSave} className="h-3.5 w-3.5 mr-2" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanRulesConfig;
